// Lightweight formatting helpers for TUR 2026.

const TR_MONTH_SHORT = [
  "Oca", "Şub", "Mar", "Nis", "May", "Haz",
  "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara",
];

export function kmText(km, digits = 0) {
  if (km === null || km === undefined || Number.isNaN(Number(km))) return "—";
  const n = Number(km);
  const fixed = Number.isInteger(n) ? String(n) : n.toFixed(digits).replace(".", ",");
  return `${fixed} km`;
}

export function dateText(isoDate) {
  if (!isoDate) return "—";
  // isoDate format: YYYY-MM-DD
  const [y, m, d] = String(isoDate).split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  return `${String(d).padStart(2, "0")} ${TR_MONTH_SHORT[m - 1]} ${y}`;
}

export function weekdayText(isoDate) {
  if (!isoDate) return "";
  try {
    return new Date(`${isoDate}T00:00:00+03:00`).toLocaleDateString("tr-TR", {
      timeZone: "Europe/Istanbul",
      weekday: "long",
    });
  } catch {
    return "";
  }
}

export function dateTimeText(iso) {
  if (!iso) return "Henüz veri yok";
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      timeZone: "Europe/Istanbul",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function shortRoute(name) {
  if (!name) return "Etap Rotası";
  return String(name).replace(/\s+-\s+/g, " → ").replace(/\s{2,}/g, " ").trim();
}

function hasStageOfficialData(stage) {
  if (!stage || typeof stage !== "object") return false;
  const hasEventResults = Array.isArray(stage.events)
    && stage.events.some((ev) => ev?.results?.hasResults && (ev?.results?.rankings || []).length > 0);
  const hasJerseys = Boolean(stage?.jerseys?.hasResults);
  const hasRankings = Array.isArray(stage?.rankingsCenter?.sections)
    && stage.rankingsCenter.sections.length > 0;
  const hasPhotoFinish = Array.isArray(stage?.photoFinish) && stage.photoFinish.length > 0;
  const hasDocuments = Array.isArray(stage?.documents) && stage.documents.length > 0;
  return hasEventResults || hasJerseys || hasRankings || hasPhotoFinish || hasDocuments;
}

export function pickInitialStageIndex(stages) {
  if (!Array.isArray(stages) || !stages.length) return 0;
  const byStageNumber = new Map(stages.map((stage, index) => [Number(stage?.stage), index]));
  const latestResultIndex = stages.reduce(
    (lastIndex, stage, index) => (hasStageOfficialData(stage) ? index : lastIndex),
    -1
  );
  const status = raceStatus(stages);

  if (status?.kind === "ongoing" && status?.stage?.stage) {
    const ongoingIndex = byStageNumber.get(Number(status.stage.stage));
    if (typeof ongoingIndex === "number") return ongoingIndex;
  }

  if ((status?.kind === "between" || status?.kind === "completed") && latestResultIndex >= 0) {
    return latestResultIndex;
  }

  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const exact = stages.findIndex((s) => s.date === today);
  if (exact >= 0) {
    if (hasStageOfficialData(stages[exact])) return exact;
    if (latestResultIndex >= 0 && latestResultIndex < exact) return latestResultIndex;
    return exact;
  }
  const next = stages.findIndex((s) => s.date && s.date > today);
  if (next >= 0) return next;
  if (latestResultIndex >= 0) return latestResultIndex;
  return 0;
}

export function normalize(s) {
  return String(s || "")
    .toLocaleLowerCase("tr-TR")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function eventGroupMeta(group) {
  switch (group) {
    case "sprint":
      return { label: "Sprint Kapısı", short: "Sprint", chip: "sprint", iconText: "S" };
    case "treasure":
      return {
        label: "Türkiye Güzellikleri Sprint Kapısı",
        short: "Türkiye Güzellikleri",
        chip: "treasure",
        iconText: "TG",
      };
    case "mountain":
      return {
        label: "Tırmanış Kapısı",
        short: "Tırmanış",
        chip: "mountain",
        iconText: "M",
      };
    case "finish":
      return { label: "Finiş", short: "Finiş", chip: "finish", iconText: "F" };
    default:
      return { label: "Etap Noktası", short: "Nokta", chip: "", iconText: "·" };
  }
}

// Official Race Guide metadata for TUR 2026.
// Times are sourced from the stage guide spreads and broadcast page.
// Elevation values summarize key route-book numbers for quick display in UI.
export const STAGE_GUIDE = {
  1: {
    signing: "09:40",
    start: "10:50",
    neutral: 6,
    finish: "14:25",
    broadcast: "12:30–14:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 20, high: 200, finish: 15 },
  },
  2: {
    signing: "11:20",
    start: "12:30",
    neutral: 10,
    finish: "16:28",
    broadcast: "14:30–16:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 40, high: 770, finish: 10 },
  },
  3: {
    signing: "11:20",
    start: "12:30",
    neutral: 1,
    finish: "16:19",
    broadcast: "14:30–16:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 15, high: 700, finish: 250 },
  },
  4: {
    signing: "12:00",
    start: "13:10",
    neutral: 1,
    finish: "16:22",
    broadcast: "14:30–16:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 15, high: 300, finish: 15 },
  },
  5: {
    signing: "10:25",
    start: "11:35",
    neutral: 5.2,
    finish: "16:21",
    broadcast: "14:30–16:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 15, high: 590, finish: 10 },
  },
  6: {
    signing: "11:10",
    start: "12:15",
    neutral: 3.1,
    finish: "16:20",
    broadcast: "14:30–16:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 45, high: 1850, finish: 1850 },
  },
  7: {
    signing: "08:40",
    start: "09:50",
    neutral: 3.3,
    finish: "13:18",
    broadcast: "11:30–13:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 25, high: 200, finish: 10 },
  },
  8: {
    signing: "09:50",
    start: "11:00",
    neutral: 3.2,
    finish: "13:21",
    broadcast: "11:30–13:30",
    broadcasters: "TRT Spor · Eurosport",
    elevation: { start: 870, high: 900, finish: 870 },
  },
};

// Official race start / estimated finish times for TUR 2026, derived from the
// stage guide object so the rest of the app can keep using the same shape.
export const STAGE_TIMES = Object.fromEntries(
  Object.entries(STAGE_GUIDE).map(([stage, guide]) => [
    Number(stage),
    {
      signing: guide.signing,
      start: guide.start,
      neutral: guide.neutral,
      finish: guide.finish,
    },
  ])
);

export function teamCategoryLabel(code) {
  const c = String(code || "").trim().toUpperCase();
  switch (c) {
    case "WTT":
      return "World";
    case "PRT":
      return "Pro";
    case "CTM":
      return "Continental";
    default:
      return code || "—";
  }
}

// Classify a stage from its event stats.
// 0 climbs → "Düz Etap"; 1-2 → "Engebeli"; 3-4 → "Tırmanış"; 5+ → "Kraliçe Etap"
export function stageClassification(stage) {
  const stats = stage?.stats || {};
  const climbs = Number(stats.mountain || 0);
  if (climbs >= 5) return { label: "Kraliçe Etap", tone: "queen" };
  if (climbs >= 3) return { label: "Tırmanış Etabı", tone: "mountain" };
  if (climbs >= 1) return { label: "Engebeli Etap", tone: "hilly" };
  return { label: "Düz Etap", tone: "flat" };
}

export function jerseyTheme(theme) {
  const v = String(theme || "").toLowerCase();
  return ["turquoise", "green", "red", "white"].includes(v) ? v : "default";
}

export function buildCountdown(targetIso) {
  // targetIso: 'YYYY-MM-DD' assumed Europe/Istanbul start of day
  if (!targetIso) return null;
  const [y, m, d] = String(targetIso).split("-").map(Number);
  if (!y || !m || !d) return null;
  // Approximate: race starts 11:00 Europe/Istanbul (UTC+3)
  const target = Date.UTC(y, m - 1, d, 8, 0, 0); // 11:00 Istanbul ≈ 08:00 UTC
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, started: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { d: days, h: hours, m: mins, s: secs, started: false };
}

function diffPartsFrom(targetMs, now = Date.now()) {
  const diff = targetMs - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

// Combine stage.date with STAGE_TIMES (Europe/Istanbul = UTC+3, no DST in TR)
export function buildStageSchedule(stages) {
  if (!Array.isArray(stages)) return [];
  return stages
    .map((s) => {
      const num = Number(s.stage);
      const t = STAGE_TIMES[num];
      if (!s.date || !t) return null;
      const [y, m, d] = String(s.date).split("-").map(Number);
      const [sh, sm] = t.start.split(":").map(Number);
      const [fh, fm] = t.finish.split(":").map(Number);
      const startMs = Date.UTC(y, m - 1, d, sh - 3, sm, 0);
      const finishMs = Date.UTC(y, m - 1, d, fh - 3, fm, 0);
      return {
        stage: num,
        startMs,
        finishMs,
        name: s.name,
        date: s.date,
        start: t.start,
        finish: t.finish,
        length: s.length,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);
}

// Returns one of:
// { kind: 'before',    target, parts, headline, subline }
// { kind: 'ongoing',   stage, parts, headline, subline }
// { kind: 'between',   target, parts, headline, subline }
// { kind: 'completed', headline, subline }
export function raceStatus(stages, now = Date.now()) {
  const schedule = buildStageSchedule(stages);
  if (!schedule.length) return { kind: "idle", headline: "—", subline: "" };
  const first = schedule[0];
  const last = schedule[schedule.length - 1];

  if (now < first.startMs) {
    return {
      kind: "before",
      target: first,
      parts: diffPartsFrom(first.startMs, now),
      headline: "Yarışa Kalan Süre",
      subline: `Etap ${first.stage} · ${shortRoute(first.name)} · ${first.start}`,
    };
  }
  if (now > last.finishMs) {
    return {
      kind: "completed",
      headline: "Yarış Tamamlandı",
      subline: `${schedule.length} etap · Tour of Türkiye 2026`,
    };
  }
  const ongoing = schedule.find((s) => now >= s.startMs && now <= s.finishMs);
  if (ongoing) {
    return {
      kind: "ongoing",
      stage: ongoing,
      parts: diffPartsFrom(ongoing.finishMs, now),
      headline: `Etap ${ongoing.stage} Sürüyor`,
      subline: `${shortRoute(ongoing.name)} · Tahmini Finiş ${ongoing.finish}`,
    };
  }
  const next = schedule.find((s) => s.startMs > now);
  if (next) {
    return {
      kind: "between",
      target: next,
      parts: diffPartsFrom(next.startMs, now),
      headline: `Etap ${next.stage}'e Kalan Süre`,
      subline: `${shortRoute(next.name)} · ${next.start}`,
    };
  }
  return { kind: "completed", headline: "Yarış Tamamlandı", subline: "" };
}
